/**
 * Storewide subscription line item flags [Kopstorm]
 *
 * Passport Global reads hidden line item properties at checkout to tell
 * subscription lines from one-time lines. Every line item leaving this
 * storefront carries both flags:
 *
 *   _is_subscription        "true" | "false"
 *   _is_one_time_purchase   "true" | "false"
 *
 * Underscore prefix keeps them hidden in the cart and on the order confirmation.
 *
 * Three layers, so it holds for the theme forms, UpCart, Replo, Zipify,
 * PageFly, the quiz, the bundle builder and any app that talks to the cart:
 *   1. fetch / XMLHttpRequest interceptor on /cart/add, /cart/change, /cart/update
 *   2. native <form action="/cart/add"> submit (capture phase)
 *   3. reconcile pass on /cart.js: any line whose flags do not match its
 *      selling plan is corrected with /cart/change (runs on load and after
 *      cart:updated, so plan switches in the drawer are covered too)
 *
 * Must load in <head> BEFORE any other script (it patches window.fetch).
 * Toggle: theme setting kop_cart_flags. Console: window.kopCartFlags.reconcile()
 */
(function () {
  'use strict';
  if (window.__kopCartFlags) return;

  var KEY_SUB = '_is_subscription';
  var KEY_OTP = '_is_one_time_purchase';
  var selfRequest = false;
  var reconcileTimer = null;
  var reconcileDepth = 0;

  function flagsFor(isSub) {
    var f = {};
    f[KEY_SUB] = isSub ? 'true' : 'false';
    f[KEY_OTP] = isSub ? 'false' : 'true';
    return f;
  }

  function hasPlan(v) {
    if (v === undefined || v === null) return false;
    v = String(v).trim();
    return v !== '' && v !== '0' && v !== 'null' && v !== 'undefined' && v !== 'false';
  }

  function isCartUrl(url, kind) {
    if (!url) return false;
    url = String(url).split('?')[0];
    return url.indexOf('/cart/' + kind) !== -1;
  }

  function classify(url) {
    if (isCartUrl(url, 'add')) return 'add';
    if (isCartUrl(url, 'change')) return 'change';
    if (isCartUrl(url, 'update')) return 'update';
    return null;
  }

  /* ---------- body rewriters ---------- */

  function tagObject(item) {
    if (!item || typeof item !== 'object') return;
    var f = flagsFor(hasPlan(item.selling_plan));
    item.properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
    item.properties[KEY_SUB] = f[KEY_SUB];
    item.properties[KEY_OTP] = f[KEY_OTP];
  }

  function tagJson(text, kind) {
    var obj;
    try { obj = JSON.parse(text); } catch (e) { return text; }
    if (!obj || typeof obj !== 'object') return text;
    if (kind === 'add') {
      if (Array.isArray(obj.items)) obj.items.forEach(tagObject);
      else if (obj.id !== undefined || obj.quantity !== undefined) tagObject(obj);
    } else if (kind === 'change') {
      /* only touch when the plan is being changed or properties are being rewritten;
         reconcile() catches the rest without dropping existing properties */
      if (Object.prototype.hasOwnProperty.call(obj, 'selling_plan') && obj.properties && typeof obj.properties === 'object') {
        tagObject(obj);
      }
    }
    return JSON.stringify(obj);
  }

  function tagParams(params, kind) {
    if (kind !== 'add') return;
    var keys = [];
    params.forEach(function (v, k) { keys.push(k); });
    var indexed = {};
    var hasIndexed = false;
    keys.forEach(function (k) {
      var m = k.match(/^items\[(\d+)\]\[/);
      if (m) { indexed[m[1]] = true; hasIndexed = true; }
    });
    if (hasIndexed) {
      Object.keys(indexed).forEach(function (i) {
        var f = flagsFor(hasPlan(params.get('items[' + i + '][selling_plan]')));
        params.set('items[' + i + '][properties][' + KEY_SUB + ']', f[KEY_SUB]);
        params.set('items[' + i + '][properties][' + KEY_OTP + ']', f[KEY_OTP]);
      });
    } else {
      var f = flagsFor(hasPlan(params.get('selling_plan')));
      params.set('properties[' + KEY_SUB + ']', f[KEY_SUB]);
      params.set('properties[' + KEY_OTP + ']', f[KEY_OTP]);
    }
  }

  function tagBody(body, kind, headers) {
    if (!body) return body;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        tagParams(body, kind);
        return body;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        tagParams(body, kind);
        return body;
      }
      if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{' || t.charAt(0) === '[') return tagJson(body, kind);
        var ct = headers && String(headers).toLowerCase();
        if (t.indexOf('=') !== -1 && (!ct || ct.indexOf('json') === -1)) {
          var p = new URLSearchParams(body);
          tagParams(p, kind);
          return p.toString();
        }
      }
    } catch (e) {}
    return body;
  }

  function headerValue(headers, name) {
    if (!headers) return '';
    try {
      if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.get(name) || '';
      if (Array.isArray(headers)) {
        for (var i = 0; i < headers.length; i++) if (String(headers[i][0]).toLowerCase() === name) return headers[i][1];
        return '';
      }
      for (var k in headers) if (String(k).toLowerCase() === name) return headers[k];
    } catch (e) {}
    return '';
  }

  /* ---------- 1. fetch ---------- */

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        var kind = classify(url);
        if (kind && !selfRequest) {
          if (init && init.body) {
            init = Object.assign({}, init);
            init.body = tagBody(init.body, kind, headerValue(init.headers, 'content-type'));
          }
          var p = nativeFetch.call(window, input, init);
          if (kind !== 'add') p.then(function () { scheduleReconcile(); }, function () {});
          return p;
        }
      } catch (e) {}
      return nativeFetch.call(window, input, init);
    };
  }

  /* ---------- 1b. XMLHttpRequest ---------- */

  if (window.XMLHttpRequest) {
    var xOpen = XMLHttpRequest.prototype.open;
    var xSend = XMLHttpRequest.prototype.send;
    var xSetHeader = XMLHttpRequest.prototype.setRequestHeader;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__kopKind = classify(url);
      this.__kopCt = '';
      return xOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
      if (String(name).toLowerCase() === 'content-type') this.__kopCt = value;
      return xSetHeader.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__kopKind && !selfRequest) {
          body = tagBody(body, this.__kopKind, this.__kopCt);
          if (this.__kopKind !== 'add') this.addEventListener('loadend', function () { scheduleReconcile(); });
        }
      } catch (e) {}
      return xSend.call(this, body);
    };
  }

  /* ---------- 2. native form submit ---------- */

  function tagForm(form) {
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    var plan = form.querySelector('[name="selling_plan"]');
    var planValue = '';
    if (plan) {
      if (plan.tagName === 'SELECT' || plan.type !== 'radio') planValue = plan.value;
      var checked = form.querySelector('[name="selling_plan"]:checked');
      if (checked) planValue = checked.value;
      if (plan.disabled) planValue = '';
    }
    var f = flagsFor(hasPlan(planValue));
    Object.keys(f).forEach(function (key) {
      var name = 'properties[' + key + ']';
      var input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-kop-cart-flag', '');
        form.appendChild(input);
      }
      input.value = f[key];
    });
  }

  document.addEventListener('submit', function (e) {
    try { tagForm(e.target); } catch (err) {}
  }, true);

  /* ---------- 3. reconcile ---------- */

  function propsEqual(props, want) {
    props = props || {};
    return props[KEY_SUB] === want[KEY_SUB] && props[KEY_OTP] === want[KEY_OTP];
  }

  function reconcile(cb) {
    if (reconcileDepth > 8) { reconcileDepth = 0; if (cb) cb(false); return; }
    selfRequest = true;
    window.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          var want = flagsFor(!!(it.selling_plan_allocation && it.selling_plan_allocation.selling_plan));
          if (propsEqual(it.properties, want)) continue;
          var props = Object.assign({}, it.properties || {}, want);
          reconcileDepth++;
          return window.fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: i + 1, quantity: it.quantity, properties: props })
          }).then(function () {
            selfRequest = false;
            reconcile(function () { if (cb) cb(true); });
          });
        }
        selfRequest = false;
        var changed = reconcileDepth > 0;
        reconcileDepth = 0;
        if (changed) {
          try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('upcart:refresh', { bubbles: true })); } catch (e) {}
        }
        if (cb) cb(changed);
      })
      .catch(function () { selfRequest = false; reconcileDepth = 0; if (cb) cb(false); });
  }

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () { reconcile(); }, 400);
  }

  ['cart:updated', 'cart:refresh', 'cart:build', 'upcart:refresh', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, function () { if (!selfRequest) scheduleReconcile(); });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleReconcile);
  else scheduleReconcile();
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleReconcile(); });

  window.__kopCartFlags = true;
  window.kopCartFlags = { reconcile: reconcile, flagsFor: flagsFor, tagForm: tagForm };
})();
